import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { baseUrl, formatDateDMY, qtyToCrates, qtyToLtrsKgs } from "../comman/config";

function DairyIssueDetailsModal({
    showModal,
    setShowModal,
    issueDetails,
    date,
    ingredient,
    sessionName,
    onIssueDeleted,
    type, // 1 for Issue, 2 for Return
    selectedDetail
}) {

    const [editMode, setEditMode] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editQuantity, setEditQuantity] = useState("");

    // What Dairy Store Issue and Dairy Store Return stored for this row: a count of theli/packets.
    // It is the Packets column, the figure Edit types into, and what the other two columns are
    // worked out from -- Ltrs / Kgs against Std. Qty. per Crate - Ltrs. / Kgs., crates against
    // Std. Qty. per Crate - Theli / Packets. While a row is being edited all three read off the
    // figure being typed, so the storekeeper sees what is about to be saved.
    const packetsOf = (issue) => {
        const stored = type === 1 ? issue.current_issued_qty : issue.current_returned_qty;
        return editMode && editingItem && editingItem.id === issue.id ? editQuantity : stored;
    };

    const handleEditClick = (issue) => {
        setEditMode(true);
        setEditingItem(issue);
        setEditQuantity(type === 1 ? issue.current_issued_qty : issue.current_returned_qty);
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        setEditingItem(null);
        setEditQuantity("");
    };

    const handleDeleteIssue = async (issueId) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this entry? This action cannot be undone.");
        if (!confirmDelete) return;

        try {
            const endpoint = type === 1
                ? `${baseUrl}/dairy_issue/delete/${issueId}`
                : `${baseUrl}/dairy_return/delete/${issueId}`;

            const response = await axios.delete(endpoint);

            if (response.data.status) {
                toast.success(`${type === 1 ? "Issue" : "Return"} entry deleted successfully`);
                onIssueDeleted(type);
            } else {
                toast.error(response.data.msg || "Failed to delete entry");
            }
        } catch (error) {
            console.error("Error deleting entry:", error);
            toast.error("Failed to delete entry");
        }
    };

    const handleSaveEdit = async () => {
        try {
            const endpoint = type === 1
                ? `${baseUrl}/dairy_issue/edit/${editingItem.id}`
                : `${baseUrl}/dairy_return/edit/${editingItem.id}`;

            const payload = type === 1
                ? { current_issued_qty: parseFloat(editQuantity) }
                : { current_returned_qty: parseFloat(editQuantity) };

            const response = await axios.post(endpoint, payload);

            if (response.data.status) {
                toast.success(`${type === 1 ? "Issue" : "Return"} entry updated successfully`);
                setEditMode(false);
                setEditingItem(null);
                setEditQuantity("");
                onIssueDeleted(type);
            } else {
                toast.error(response.data.msg || "Failed to update entry");
            }
        } catch (error) {
            console.error("Error updating entry:", error);
            toast.error("Failed to update entry");
        }
    };

    // The date half reads DD-MM-YYYY like every other date on the dairy screens; toLocaleDateString
    // would hand a browser set to en-US the ambiguous 9/4/2026 instead.
    const formatDateTime = (dateTimeString) => {
        const dt = new Date(dateTimeString);
        return `${formatDateDMY(dateTimeString)} ${dt.toLocaleTimeString()}`;
    };

    if (!showModal) return null;

    const headingType = type === 1 ? "Issue" : "Return";

    console.log("issue detail:", issueDetails);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">
                        {headingType} Details - {ingredient}
                        {sessionName && ` (${sessionName})`} on {formatDateDMY(date)}
                    </h2>
                    <button
                        onClick={() => setShowModal(false)}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {issueDetails.length === 0 ? (
                    <p className="text-center py-4">No {headingType.toLowerCase()} details found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left border-collapse border border-gray-300">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 border border-gray-300">Time</th>
                                    <th className="px-4 py-2 border border-gray-300">Menu Item</th>
                                    <th className="px-4 py-2 border border-gray-300">Kit Numbers</th>
                                    <th className="px-4 py-2 border border-gray-300">{type === 1 ? "Issued Qty" : "Returned Qty"} (Ltrs / Kgs)</th>
                                    <th className="px-4 py-2 border border-gray-300">Crates</th>
                                    <th className="px-4 py-2 border border-gray-300">Packets</th>
                                    <th className="px-4 py-2 border border-gray-300">Remarks</th>
                                    <th className="px-4 py-2 border border-gray-300">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {issueDetails.map((issue) => (
                                    <tr key={issue.id} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-2 border border-gray-300">
                                            {formatDateTime(issue.created_at)}
                                        </td>
                                        <td className="px-4 py-2 border border-gray-300">
                                            {issue.menu_item_name || "-"}
                                        </td>
                                        <td className="px-4 py-2 border border-gray-300">
                                            {issue.kit_numbers
                                                ? issue.kit_numbers.map(kit => kit.kit_no).join(", ")
                                                : "-"}
                                        </td>
                                        <td className="px-4 py-2 border border-gray-300">
                                            {qtyToLtrsKgs(packetsOf(issue), issue.std_qty_per_crate)}
                                        </td>
                                        <td className="px-4 py-2 border border-gray-300">
                                            {qtyToCrates(packetsOf(issue), issue.std_qty_per_crate_theli) || issue.crates || "-"}
                                        </td>
                                        <td className="px-4 py-2 border border-gray-300">
                                            {editMode && editingItem && editingItem.id === issue.id ? (
                                                <input
                                                    type="number"
                                                    className="w-20 p-1 border border-gray-300 rounded"
                                                    value={editQuantity}
                                                    onChange={(e) => setEditQuantity(e.target.value)}
                                                    step="0.01"
                                                    min="0"
                                                />
                                            ) : (
                                                packetsOf(issue)
                                            )}
                                        </td>
                                        <td className="px-4 py-2 border border-gray-300">
                                            {issue.remarks || "-"}
                                        </td>
                                        <td className="px-4 py-2 border border-gray-300">
                                            {editMode && editingItem && editingItem.id === issue.id ? (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={handleSaveEdit}
                                                        className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-2 rounded text-xs"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={handleCancelEdit}
                                                        className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-1 px-2 rounded text-xs"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleEditClick(issue)}
                                                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded text-xs"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteIssue(issue.id)}
                                                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex justify-end mt-4">
                    <button
                        onClick={() => setShowModal(false)}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DairyIssueDetailsModal;
